import {Component, ViewChild} from '@angular/core';
import {KENDO_DROPDOWNS} from '@progress/kendo-angular-dropdowns';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';

import {BoiteDialogueGeneriqueComponent, BoiteDialogueMessagesComponent, ControleConteneurComponent, SimplePanelComponent} from '../../../../../../val-ammon-composants/val-composants-lib/src/public-api';
import {AeFMettreAJourCorbeilleGedFacturesAchatParametres, AeFMettreAJourCorbeilleGedFacturesAchatResultat, EFactTraitementService} from '../../../../../../val-ammon-services/val-services-efact-lib/src/public-api';
import {ValApiResultat} from '../../../../../../val-ammon-services/val-services-communs-lib/src/public-api';
import {EFactParametragesService} from '../../val-efact-parametrages.service';
import {CorbeilleGedEFact} from '../../RetournerCorbeillesGedResultat';

//Clé de traduction du titre de l’envoi vers une corbeille — repris dans la boîte de choix et dans la boîte de restitution
const c_TitreEnvoyer = "achat.corbeille-ged.envoyer-titre";

//Clé de traduction du titre du retrait de la corbeille — repris dans la boîte de confirmation et dans la boîte de restitution
const c_TitreRetirer = "achat.corbeille-ged.retirer-titre";

//Traitement d’affectation manuelle des factures d’achat à une corbeille GED (SU #120249)
//  Pose ou retire, sur la sélection, la corbeille GED de destination que le job de marquage pose
//  sinon par règle. Le dépôt lui-même reste celui du transfert des documents : ce traitement ne
//  décide que de la destination.
//  Les deux gestes sont deux entrées de menu, servies par la même boîte : choix de la corbeille pour
//  l’envoi, simple confirmation pour le retrait.
@Component (
{
  selector: 'val-corbeille-ged-factures-achat',
  imports:
  [
    KENDO_DROPDOWNS,
    BoiteDialogueGeneriqueComponent,
    BoiteDialogueMessagesComponent,
    SimplePanelComponent,
    ControleConteneurComponent,
    TranslocoPipe
  ],
  templateUrl: './corbeille-ged-factures-achat.component.html',
  styleUrl: './corbeille-ged-factures-achat.component.scss'
})
export class CorbeilleGedFacturesAchatComponent
{
  //Création
  //  Paramètres
  //    eFactTraitementService   : Service e-Facture
  //    eFactParametragesService : Service de paramétrage e-Facture
  //    translocoService         : Service de traduction
  constructor (private eFactTraitementService: EFactTraitementService, private eFactParametragesService: EFactParametragesService,
               private translocoService: TranslocoService)
  {
    this.titre = c_TitreEnvoyer;
    this.retrait = false;
    this.tailleLabels = 110;
    this.nombreFacturesSelectionnees = 0;
    this.corbeillesGed = [];
    this.corbeilleSelectionnee = null;
    this.confirme = false;
  }

  //Boîte de dialogue de choix de la corbeille, ou de confirmation du retrait
  @ViewChild ("boiteDialogue")
  public boiteDialogue!: BoiteDialogueGeneriqueComponent<void>;

  //Boîte de dialogue de restitution
  @ViewChild ("boiteDialogueMessages")
  public boiteDialogueMessages!: BoiteDialogueMessagesComponent;

  //Clé de traduction du titre du traitement en cours
  public titre: string;

  //Vrai pour le retrait de la corbeille, faux pour l’envoi vers une corbeille
  public retrait: boolean;

  //Taille des labels
  public tailleLabels: number;

  //Nombre de factures de la sélection en cours — affiché pour que l’utilisateur sache sur quoi il agit
  public nombreFacturesSelectionnees: number;

  //Corbeilles GED proposées, actives seulement
  public corbeillesGed: DonneesCorbeilleGedProposee[];

  //Corbeille sélectionnée
  public corbeilleSelectionnee: DonneesCorbeilleGedProposee | null;

  //Vrai si l’utilisateur a validé la boîte
  private confirme: boolean;

  //Envoie les factures sélectionnées vers une corbeille GED choisie par l’utilisateur
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées dans la grille
  //  Retourne
  //    Vrai si au moins une facture a changé de corbeille — la grille doit alors être rafraîchie
  public async Envoyer (identifiantsFactures: string[]) : Promise<boolean>
  {
    if (identifiantsFactures.length === 0)
      return false;

    this.titre = c_TitreEnvoyer;
    this.retrait = false;

    if (!await this.ChargerCorbeillesGed ())
      return false;

    if (!await this.OuvrirBoite (identifiantsFactures.length) || this.corbeilleSelectionnee === null)
      return false;

    return this.Appliquer (identifiantsFactures, this.corbeilleSelectionnee.identifiant);
  }

  //Retire la corbeille GED de destination des factures sélectionnées
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées dans la grille
  //  Retourne
  //    Vrai si au moins une facture a perdu sa corbeille — la grille doit alors être rafraîchie
  public async Retirer (identifiantsFactures: string[]) : Promise<boolean>
  {
    if (identifiantsFactures.length === 0)
      return false;

    this.titre = c_TitreRetirer;
    this.retrait = true;
    this.corbeillesGed = [];
    this.corbeilleSelectionnee = null;

    if (!await this.OuvrirBoite (identifiantsFactures.length))
      return false;

    return this.Appliquer (identifiantsFactures, null);
  }

  //Charge les corbeilles GED affectables du client
  //  Seules les corbeilles actives sont proposées : une corbeille désactivée ne reçoit plus aucune
  //  facture, ni par règle ni à la main.
  //  Retourne
  //    Vrai si au moins une corbeille est proposable ; sinon, l’utilisateur en a été averti
  private async ChargerCorbeillesGed () : Promise<boolean>
  {
    let valApiResultat: ValApiResultat<CorbeilleGedEFact[]>;

    this.corbeillesGed = [];
    this.corbeilleSelectionnee = null;

    try
    {
      valApiResultat = await this.eFactParametragesService.RetournerCorbeillesGed ();

      if (valApiResultat.status !== "success")
      {
        await this.Restituer ("exclamation.png",
                              [valApiResultat.message && valApiResultat.message.length > 0
                                 ? valApiResultat.message
                                 : this.translocoService.translate ("achat.commun.traitement-echoue", {titre: this.translocoService.translate (this.titre)})]);

        return false;
      }

      this.corbeillesGed = (valApiResultat.resultat ?? [])
        .filter (corbeille => corbeille.actif)
        .map (corbeille => ({identifiant: corbeille.id, libelle: `${corbeille.libelle} (${corbeille.code})`}));
    }
    catch (erreur)
    {
      await this.RestituerErreurTechnique (erreur);

      return false;
    }

    if (this.corbeillesGed.length === 0)
    {
      await this.Restituer ("information.png", [this.translocoService.translate ("achat.corbeille-ged.aucune-corbeille-active")]);

      return false;
    }

    //Une seule corbeille : la présélectionner épargne un choix qui n’en est pas un.
    if (this.corbeillesGed.length === 1)
      this.corbeilleSelectionnee = this.corbeillesGed[0];

    return true;
  }

  //Ouvre la boîte de choix ou de confirmation
  //  Paramètres
  //    nombreFactures : Nombre de factures de la sélection
  //  Retourne
  //    Vrai si l’utilisateur a validé
  private async OuvrirBoite (nombreFactures: number) : Promise<boolean>
  {
    this.nombreFacturesSelectionnees = nombreFactures;
    this.confirme = false;

    await this.boiteDialogue.Ouvrir ();

    return this.confirme;
  }

  //Applique la corbeille cible sur la sélection
  //  Paramètres
  //    identifiantsFactures    : Identifiants des factures sélectionnées dans la grille
  //    identifiantCorbeilleGed : Identifiant de la corbeille cible, nul pour retirer la corbeille
  //  Retourne
  //    Vrai si au moins une facture a changé de corbeille
  private async Appliquer (identifiantsFactures: string[], identifiantCorbeilleGed: string | null) : Promise<boolean>
  {
    let aefMettreAJourCorbeilleGedFacturesAchatParametres: AeFMettreAJourCorbeilleGedFacturesAchatParametres;
    let valApiResultat: ValApiResultat<AeFMettreAJourCorbeilleGedFacturesAchatResultat>;

    try
    {
      aefMettreAJourCorbeilleGedFacturesAchatParametres = new AeFMettreAJourCorbeilleGedFacturesAchatParametres ();
      aefMettreAJourCorbeilleGedFacturesAchatParametres.identifiantsFactures = identifiantsFactures;
      aefMettreAJourCorbeilleGedFacturesAchatParametres.identifiantCorbeilleGed = identifiantCorbeilleGed;

      valApiResultat = await this.eFactTraitementService.MettreAJourCorbeilleGedFacturesAchat (aefMettreAJourCorbeilleGedFacturesAchatParametres);

      if (valApiResultat.status !== "success")
      {
        await this.Restituer ("exclamation.png",
                              [valApiResultat.message && valApiResultat.message.length > 0
                                 ? valApiResultat.message
                                 : this.translocoService.translate ("achat.commun.traitement-echoue", {titre: this.translocoService.translate (this.titre)})]);

        return false;
      }

      await this.RestituerSucces (valApiResultat.resultat ?? new AeFMettreAJourCorbeilleGedFacturesAchatResultat ());

      return (valApiResultat.resultat?.nombreFacturesModifiees ?? 0) > 0;
    }
    catch (erreur)
    {
      await this.RestituerErreurTechnique (erreur);

      return false;
    }
  }

  //Restitue le résultat d’un traitement réussi
  //  Paramètres
  //    resultat : Résultat du traitement
  private async RestituerSucces (resultat: AeFMettreAJourCorbeilleGedFacturesAchatResultat) : Promise<void>
  {
    let messages: string[];

    messages = [this.translocoService.translate ("achat.corbeille-ged.factures-modifiees", {nombre: resultat.nombreFacturesModifiees})];

    //Les factures déjà dans l’état demandé ne sont pas retouchées : le dire évite de faire chercher
    //une trace de modification qui n’existe pas.
    if (resultat.nombreFacturesInchangees > 0)
      messages.push (this.translocoService.translate ("achat.commun.factures-inchangees", {nombre: resultat.nombreFacturesInchangees}));

    //Les factures écartées le sont nommément, avec le motif rédigé par le serveur, affiché tel quel :
    //les taire laisserait croire que toute la sélection a suivi.
    if (resultat.facturesEcartees.length > 0)
    {
      messages.push (this.translocoService.translate ("achat.corbeille-ged.factures-ecartees", {nombre: resultat.facturesEcartees.length}));

      for (const ecartee of resultat.facturesEcartees)
        messages.push (`• ${ecartee.numero} — ${ecartee.motif}`);
    }

    await this.Restituer ("information.png", messages);
  }

  //Restitue une erreur technique
  //  Paramètres
  //    erreur : Erreur interceptée
  private async RestituerErreurTechnique (erreur: unknown) : Promise<void>
  {
    let messages: string[];

    messages = [this.translocoService.translate ("achat.commun.erreur-technique-traitement", {titre: this.translocoService.translate (this.titre)})];

    if (erreur != null && typeof (erreur as {message?: unknown}).message === "string")
      messages.push ((erreur as {message: string}).message);

    await this.Restituer ("exclamation.png", messages);
  }

  //Ouvre la boîte de restitution
  //  Paramètres
  //    icone    : Icone de la boîte
  //    messages : Messages à afficher
  private async Restituer (icone: string, messages: string[]) : Promise<void>
  {
    this.boiteDialogueMessages.titre = this.translocoService.translate (this.titre);
    this.boiteDialogueMessages.icone = icone;
    this.boiteDialogueMessages.messages = messages;

    await this.boiteDialogueMessages.Ouvrir ();
  }

  //Valide le choix ou la confirmation
  public Valider () : void
  {
    this.confirme = true;

    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }

  //Annule le traitement
  public Annuler () : void
  {
    this.confirme = false;

    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }
}

//Représente une corbeille GED proposée à l’utilisateur
export class DonneesCorbeilleGedProposee
{
  //Identifiant de la corbeille
  public identifiant!: string;

  //Libellé présenté dans la liste : « libellé (code) »
  public libelle!: string;
}
